import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

/**
 * Reusable Confirmation Dialog Component
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message content
 * @param {function} onConfirm - Callback when user confirms
 * @param {function} onCancel - Callback when user cancels
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {boolean} loading - Show loading state (default: false)
 * @param {string} variant - Dialog variant: 'warning', 'danger', 'success', 'info' (default: 'warning')
 * @param {object} container - Container element for portal (optional)
 * @param {number} zIndex - Custom z-index for dialog (optional, default: 1600)
 */
const ConfirmationDialog = ({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  variant = 'warning',
  container,
  zIndex = 1600,
}) => {
  const getVariantConfig = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: <WarningIcon fontSize="small" />,
          color: '#f44336',
          hoverColor: '#c62828',
          titleColor: '#f44336',
        };
      case 'success':
        return {
          icon: <CheckCircleIcon fontSize="small" />,
          color: '#4caf50',
          hoverColor: '#388e3c',
          titleColor: '#4caf50',
        };
      case 'info':
        return {
          icon: <InfoIcon fontSize="small" />,
          color: '#2196f3',
          hoverColor: '#1976d2',
          titleColor: '#2196f3',
        };
      case 'warning':
      default:
        return {
          icon: <WarningIcon fontSize="small" />,
          color: '#ff9800',
          hoverColor: '#f57c00',
          titleColor: '#ff9800',
        };
    }
  };

  const config = getVariantConfig();

  return (
    <Dialog
      open={open}
      onClose={onCancel || undefined}
      maxWidth="sm"
      fullWidth
      container={container}
      BackdropProps={{
        sx: {
          zIndex: zIndex - 1, // Backdrop appears just behind the dialog
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          zIndex: zIndex, // Paper (dialog content) has the specified z-index
        },
      }}
      sx={{
        zIndex: zIndex, // Dialog root has the specified z-index
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontWeight: 700,
          fontSize: '1.1rem',
          color: config.titleColor,
          pb: 1.5,
        }}
      >
        {config.icon}
        {title}
      </DialogTitle>
      
      <DialogContent sx={{ pb: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.7,
            whiteSpace: 'pre-line',
          }}
        >
          {message}
        </Typography>
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
        {onCancel && (
          <Button
            onClick={onCancel}
            color="inherit"
            disabled={loading}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {cancelText}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
            bgcolor: config.color,
            '&:hover': {
              bgcolor: config.hoverColor,
            },
            '&.Mui-disabled': {
              bgcolor: '#e0e0e0',
              color: '#9e9e9e',
            },
          }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
